
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { Validate } from '@/types';
import { findSupportedLocale } from '@/utils/i18n-helpers';
import Toast from '@/utils/toast';
import SelectComponent from '../shared/Select.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextField from '@/components/shared/TextField.vue';

@Component({
	components: {
		SelectComponent,
		StandardModal,
		TextField,
		ValidationObserver,
		ValidationProvider
	}
})
export default class ModifySchoolDeliveryModal extends Vue {
	@Action('modifySchoolDeliveryInfo', { namespace: 'orders' }) private modifySchoolDeliveryInfo!: (payload: ModifySchoolDeliveryPayload) => Promise<void>;
	@Prop({ type: Boolean, required: true, default: false }) private showModal!: boolean;
	@Prop({ type: Number, required: true }) private orderId!: number;
	@Prop({ type: Object, required: true }) private deliveryInfo!: DeliveryInfo;
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;

	$refs!: { observer: Validate; };

	private mutableSchoolDeliveryInfo: DeliveryInfo = {
		type: 'school',
		school: '',
		grade: '',
		classroom: '',
		teacher: '',
		address: '',
		extra: ''
	};
	private modifyAllOrders: boolean = true;
	private loading: boolean = false;

	/**
	 * Splits classrooms, grades, teachers from the school delivery information into arrays
	 *
	 * @return {SchoolDeliveryOptions | undefined} - Returns an object with split values or undefined
	 */
	private get schoolDeliveryOptions(): SchoolDeliveryOptions | undefined {
		const schools = this.restaurant.delivery?.schools;
		const school: SchoolDeliveryElement | undefined = schools?.length === 1
			? schools[0]
			: schools?.find((school: SchoolDeliveryElement) => school.name === this.mutableSchoolDeliveryInfo.school);

		if (school) {
			return Object.keys(school).reduce((acc: SchoolDeliveryOptions, key: string) => {
				if (key === 'classrooms' || key === 'grades') {
					acc[key] = school[key]?.split(',').map((k: string) => k.trim()) || [];
				}
				if (key === 'teachers') {
					acc.isTeachersOpenEnded = school.teachers?.length === 0;
					acc.teachers = acc.isTeachersOpenEnded ? '' : school.teachers?.split(',').map((k: string) => k.trim());

					// Remove teacher from mutableSchoolDeliveryInfo if it is not in the list of teachers
					if (!acc.isTeachersOpenEnded && this.mutableSchoolDeliveryInfo.teacher && !acc.teachers?.includes(this.mutableSchoolDeliveryInfo.teacher)) {
						this.mutableSchoolDeliveryInfo.teacher = '';
					}
				}
				return acc;
			}, {});
		}
	}

	private created(): void {
		this.mutableSchoolDeliveryInfo = cloneDeep(this.deliveryInfo);
		if (this.restaurant.delivery?.schools?.length === 1) {
			this.mutableSchoolDeliveryInfo.school = this.restaurant.delivery.schools[0].name;
		}
	}

	/**
	 * Compare the school delivery info with the restaurant's delivery settings
	 * Filter out any editable fields that are not in the restaurant's delivery settings
	 *
	 * @return {DeliveryInfo}
	 */
	private formatSchoolDeliveryInfo(): DeliveryInfo {
		const filteredDeliveryInfo = Object.keys(this.mutableSchoolDeliveryInfo).reduce((acc: DeliveryInfo, key: string) => {
			if (['grade', 'classroom', 'teacher'].includes(key) && this.schoolDeliveryOptions && !Object.keys(this.schoolDeliveryOptions).find((k: string) => k.includes(key))) {
				delete acc[key as keyof DeliveryInfo];
			}
			return acc;
		}, cloneDeep(this.mutableSchoolDeliveryInfo));

		// Update address and localization based on the filtered delivery info
		return {
			...filteredDeliveryInfo,
			...this.updateDeliveryAddress(filteredDeliveryInfo)
		};
	}

	/**
	 * Updates the delivery address by creating a string from the mutableSchoolDeliveryInfo object
	 *
	 * @param {DeliveryInfo} deliveryInfo - Filtered delivery information
	 * @return {Partial<DeliveryInfo>}
	 */
	private updateDeliveryAddress(deliveryInfo: DeliveryInfo): Partial<DeliveryInfo> {
		const addressObj: { address: string, localization?: { [key: string]: LocalizationInfo } } = {
			address: '',
			localization: {}
		};
		const locales: RestaurantLocale[] | null = this.restaurant.locales;
		if (locales?.length) {
			locales.forEach((locale: RestaurantLocale) => {
				const supportedLocale: Locale | undefined = findSupportedLocale('', locale.locale_short);
				if (supportedLocale && locale.is_default) {
					addressObj.address = this.localizeAddress(deliveryInfo, supportedLocale.code);
				}
				else if (supportedLocale) {
					addressObj.localization![locale.locale_short] = {
						address: this.localizeAddress(deliveryInfo, supportedLocale.code)
					};
				}
			});
		}
		else {
			// If no locales are set it is assumed that en-CA is the default
			addressObj.address = this.localizeAddress(deliveryInfo, 'en-CA');
		}
		// Remove localization property if it is empty
		if (!Object.keys(addressObj.localization!).length) {
			delete addressObj.localization;
		}
		return addressObj;
	}

	/**
	 * Localizes the address for given locale
	 *
	 * @param {DeliveryInfo} deliveryInfo
	 * @param {string} locale
	 * @return {string}
	 */
	private localizeAddress(deliveryInfo: DeliveryInfo, locale: string): string {
		const addressParts: string[] = [];
		const schoolFields: (keyof DeliveryInfo)[] = ['school', 'grade', 'classroom', 'student', 'teacher', 'extra'];
		schoolFields.forEach((key: string) => {
			const value = deliveryInfo[key as keyof DeliveryInfo];
			if (value) {
				addressParts.push(this.$t(`orders.management.modify_school_delivery_modal.${key}_address`, locale, { [key]: value }));
			}
		});
		return addressParts.join(', ');
	}

	/**
	 * Validate and save the modified school delivery info
	 *
	 * @return {Promise<void>}
	 */
	private async validateAndSave(): Promise<void> {
		try {
			const valid = await this.$refs.observer.validate();
			if (valid) {
				this.loading = true;
				await this.modifySchoolDeliveryInfo({
					id: this.orderId,
					delivery: this.formatSchoolDeliveryInfo(),
					modifyAllOrders: this.modifyAllOrders
				});
				this.loading = false;
				this.$emit('confirm');
			}
		}
		catch (errorMessage) {
			this.loading = false;
			Toast.error(this.$t('orders.management.modify_school_delivery_modal.error_message', { errorMessage }));
		}
	}
}
